import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-white dark:bg-black" }
const _hoisted_2 = { class: "lg:pl-[17rem]" }
const _hoisted_3 = { class: "sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white dark:bg-black shadow" }
const _hoisted_4 = { class: "flex flex-1 items-center justify-between px-4" }
const _hoisted_5 = { class: "flex flex-1 items-center" }
const _hoisted_6 = {
  key: 0,
  class: "flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-0.5"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "flex items-center mr-4" }
const _hoisted_9 = { class: "flex items-center" }
const _hoisted_10 = { class: "relative ml-3" }
const _hoisted_11 = { class: "h-8 w-8 rounded-full flex items-center justify-center text-white" }
const _hoisted_12 = {
  key: 0,
  class: "absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-black py-1 shadow-lg ring-1 ring-black ring-opacity-5"
}
const _hoisted_13 = { class: "px-4 py-2 text-sm text-gray-700 dark:text-gray-200" }
const _hoisted_14 = { class: "font-medium truncate" }
const _hoisted_15 = { class: "text-gray-500 dark:text-gray-400 truncate" }
const _hoisted_16 = { class: "flex items-center" }
const _hoisted_17 = { class: "mr-3 text-lg" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "flex items-center gap-2" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "flex items-center" }
const _hoisted_22 = { class: "py-2 lg:py-6" }
const _hoisted_23 = { class: "w-full px-2 md:px-6 lg:px-6" }

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useClickOutside } from '@/composables/ui/useClickOutside'
import { useUser } from '@/composables/auth/useUser'
import { useLogin } from '@/composables/auth/useLogin'
import { useBrand } from '@/composables/brand/useBrand'
import { BRANDS } from '@/config/brands'
import Sidebar from './Sidebar.vue'
import NotificationDropdown from './NotificationDropdown.vue'
import CompleteProfileModal from '@/components/customer/CompleteProfileModal.vue'

// Store Instances

export default /*@__PURE__*/_defineComponent({
  __name: 'AppLayout',
  setup(__props) {

const authStore = useAuthStore()
const themeStore = useThemeStore()
const router = useRouter()
const { userInitials } = useUser()
const { handleLogout } = useLogin()
const { currentBrand, brandColors } = useBrand()

// State
const isProfileOpen = ref(false)
const isSidebarOpen = ref(false)
const showCompleteProfileModal = ref(false)

// Computed Properties
const currentUser = computed(() => authStore.currentUser)
const isLoading = computed(() => authStore.isLoading)
const isDarkMode = computed(() => themeStore.isDarkMode)
const isInCustomerView = computed(() => authStore.isInCustomerView)

// All available brands for the switch menu
const availableBrands = computed(() => BRANDS)

// The name of the brand currently active in customer view
const activeBrandName = computed(() => {
  if (!authStore.adminViewBrandId) return ''
  return BRANDS.find(b => b.id === authStore.adminViewBrandId)?.name ?? ''
})

// Dynamic Styles
const headerStyle = computed(() => ({
  backgroundColor: '#ffffff',
  borderBottomWidth: '1px',
  borderBottomColor: brandColors.value.accent + '20',
}))

// Methods
const toggleProfile = () => {
  isProfileOpen.value = !isProfileOpen.value
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleTheme = () => {
  themeStore.toggleTheme()
}

const handleSwitchToCustomer = (brandId: number) => {
  isProfileOpen.value = false
  authStore.switchToCustomerView(brandId)
  router.push({ name: 'tcp' })
}

const handleSwitchToAdmin = () => {
  isProfileOpen.value = false
  authStore.switchToAdminView()
  router.push({ name: 'admin-dashboard' })
}

// Lifecycle Hooks
const { handleClickOutside } = useClickOutside(isProfileOpen)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)

  // Check if customer profile is incomplete
  const user = authStore.currentUser
  if (user && user.role === 'customer' && (!user.mobile || !user.city)) {
    showCompleteProfileModal.value = true
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const goToCompleteProfile = () => {
  showCompleteProfileModal.value = false
  router.push({ name: 'customer-info' })
}

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(Sidebar, {
        isOpen: isSidebarOpen.value,
        "onUpdate:isOpen": _cache[0] || (_cache[0] = ($event: any) => ((isSidebarOpen).value = $event))
      }, null, 8, ["isOpen"]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("header", _hoisted_3, [
          _createElementVNode("button", {
            type: "button",
            class: "px-4 focus:outline-none focus:ring-2 focus:ring-inset lg:hidden",
            style: _normalizeStyle({ color: _unref(brandColors).accent }),
            onClick: toggleSidebar
          }, _cache[2] || (_cache[2] = [
            _createElementVNode("span", { class: "sr-only" }, "Open sidebar", -1),
            _createElementVNode("svg", {
              class: "h-6 w-6",
              xmlns: "http://www.w3.org/2000/svg",
              fill: "none",
              viewBox: "0 0 24 24",
              stroke: "currentColor"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M4 6h16M4 12h16M4 18h16"
              })
            ], -1)
          ]), 4),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              (currentUser.value?.role === 'admin' && !isInCustomerView.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableBrands.value, (brand) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: brand.id,
                        type: "button",
                        class: "px-4 py-1 text-xs font-semibold rounded-full transition-all duration-200 focus:outline-none",
                        style: _normalizeStyle(_unref(authStore).adminBrandId === brand.id
                  ? { backgroundColor: brand.color, color: brand.buttonTextColor, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }
                  : { backgroundColor: 'transparent', color: '#6b7280' }),
                        onClick: ($event: any) => (_unref(authStore).setAdminBrand(brand.id))
                      }, _toDisplayString(brand.name), 13, _hoisted_7))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("p", {
                class: "text-sm md:text-xl font-medium tracking-wider",
                style: _normalizeStyle({ color: _unref(brandColors).primary })
              }, _toDisplayString(_unref(currentBrand)?.programName ?? 'TOTAL CONFIDENCE PLAN'), 5)
            ]),
            (currentUser.value?.role === 'admin' && !isInCustomerView.value)
              ? (_openBlock(), _createBlock(NotificationDropdown, { key: 0 }))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("div", null, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "flex max-w-xs items-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-offset-2",
                    style: _normalizeStyle({
                    backgroundColor: _unref(brandColors).accent,
                    '--tw-ring-color': _unref(brandColors).accent
                  }),
                    onClick: toggleProfile
                  }, [
                    _cache[3] || (_cache[3] = _createElementVNode("span", { class: "sr-only" }, "Open user menu", -1)),
                    _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(userInitials)), 1)
                  ], 4)
                ]),
                (isProfileOpen.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("p", _hoisted_14, _toDisplayString(currentUser.value?.first_name) + " " + _toDisplayString(currentUser.value?.last_name), 1),
                        _createElementVNode("p", _hoisted_15, _toDisplayString(currentUser.value?.email), 1),
                        _createElementVNode("p", {
                          class: "text-xs mt-1 px-2 py-0.5 rounded-full inline-block text-white",
                          style: _normalizeStyle({ backgroundColor: _unref(brandColors).accent })
                        }, _toDisplayString(isInCustomerView.value ? 'customer view' : currentUser.value?.role), 5)
                      ]),
                      _cache[8] || (_cache[8] = _createElementVNode("div", { class: "border-t border-gray-100 dark:border-gray-700" }, null, -1)),
                      _createElementVNode("button", {
                        type: "button",
                        class: "block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700",
                        onClick: toggleTheme
                      }, [
                        _createElementVNode("div", _hoisted_16, [
                          _createElementVNode("span", _hoisted_17, _toDisplayString(isDarkMode.value ? '🌞' : '🌙'), 1),
                          _createElementVNode("span", null, _toDisplayString(isDarkMode.value ? 'Light Mode' : 'Dark Mode'), 1)
                        ])
                      ]),
                      (currentUser.value?.role === 'admin')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _cache[5] || (_cache[5] = _createElementVNode("div", { class: "border-t border-gray-100 dark:border-gray-700" }, null, -1)),
                            (!isInCustomerView.value)
                              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(availableBrands.value, (brand) => {
                                  return (_openBlock(), _createElementBlock("button", {
                                    key: brand.id,
                                    type: "button",
                                    class: "block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700",
                                    onClick: ($event: any) => (handleSwitchToCustomer(brand.id))
                                  }, [
                                    _createElementVNode("div", _hoisted_19, [
                                      _createElementVNode("span", {
                                        class: "inline-block w-2.5 h-2.5 rounded-full flex-shrink-0",
                                        style: _normalizeStyle({ backgroundColor: brand.color })
                                      }, null, 4),
                                      _createElementVNode("span", null, "Customer View — " + _toDisplayString(brand.name), 1)
                                    ])
                                  ], 8, _hoisted_18))
                                }), 128))
                              : (_openBlock(), _createElementBlock("button", {
                                  key: 1,
                                  type: "button",
                                  class: "block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700",
                                  onClick: handleSwitchToAdmin
                                }, _cache[4] || (_cache[4] = [
                                  _createElementVNode("div", { class: "flex items-center" }, [
                                    _createElementVNode("svg", {
                                      class: "mr-3 h-4 w-4 text-gray-400",
                                      fill: "none",
                                      viewBox: "0 0 24 24",
                                      stroke: "currentColor",
                                      "stroke-width": "2"
                                    }, [
                                      _createElementVNode("path", {
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round",
                                        d: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                      })
                                    ]),
                                    _createElementVNode("span", null, "Back to Admin")
                                  ], -1)
                                ]))),
                            _cache[6] || (_cache[6] = _createElementVNode("div", { class: "border-t border-gray-100 dark:border-gray-700" }, null, -1))
                          ], 64))
                        : _createCommentVNode("", true),
                      _createElementVNode("button", {
                        type: "button",
                        class: "block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50",
                        onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(handleLogout) && _unref(handleLogout)(...args))),
                        disabled: isLoading.value
                      }, [
                        _createElementVNode("div", _hoisted_21, [
                          _cache[7] || (_cache[7] = _createElementVNode("svg", {
                            class: "mr-3 h-5 w-5 text-gray-400",
                            xmlns: "http://www.w3.org/2000/svg",
                            viewBox: "0 0 20 20",
                            fill: "currentColor"
                          }, [
                            _createElementVNode("path", {
                              "fill-rule": "evenodd",
                              d: "M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v3a1 1 0 102 0V9z",
                              "clip-rule": "evenodd"
                            })
                          ], -1)),
                          _createElementVNode("span", null, _toDisplayString(isLoading.value ? 'Logging out...' : 'Sign out'), 1)
                        ])
                      ], 8, _hoisted_20)
                    ]))
                  : _createCommentVNode("", true)
              ])
            ])
          ])
        ]),
        (isInCustomerView.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "flex items-center justify-center gap-2 py-1.5 text-xs font-medium text-white",
              style: _normalizeStyle({ backgroundColor: _unref(brandColors).primary })
            }, [
              _cache[9] || (_cache[9] = _createElementVNode("svg", {
                class: "w-3.5 h-3.5",
                fill: "none",
                viewBox: "0 0 24 24",
                stroke: "currentColor",
                "stroke-width": "2"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  d: "M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                }),
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  d: "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                })
              ], -1)),
              _createTextVNode(" You are viewing as a " + _toDisplayString(activeBrandName.value) + " Customer. Actions will be recorded under your admin account. ", 1)
            ], 4))
          : _createCommentVNode("", true),
        _createElementVNode("main", _hoisted_22, [
          _createElementVNode("div", _hoisted_23, [
            _createVNode(_component_router_view)
          ])
        ])
      ])
    ]),
    _createVNode(CompleteProfileModal, {
      "is-open": showCompleteProfileModal.value,
      onComplete: goToCompleteProfile
    }, null, 8, ["is-open"])
  ], 64))
}
}

})